/**
 * Modelos de leitura de arquivo.
 *
 * Duas abas, porque são dois públicos:
 *
 * - **Cadastrados** — todos veem. É onde se enxerga quais operadoras já aceitam
 *   lançamento e qual a procedência de cada modelo.
 * - **Aguardando aprovação** — só o ADMIN. É a fila que o fluxo assistido
 *   alimenta, e o único lugar onde um modelo é liberado para uso.
 *
 * A regra que governa a tela: um modelo só fica disponível para lançamento
 * depois da aprovação do admin, e essa aprovação é ancorada nas conferências
 * aritméticas do próprio documento — nunca em inspeção visual da tabulação.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import {
  aprovarModelo,
  detalharModelo,
  iaDisponivel,
  excluirModelo,
  inativarModelo,
  listarTodosModelos,
  rejeitarModelo,
  submeterConjunto,
  submeterModelo,
} from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  LoadingSpinner,
  PageHeader,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { Conferencias, Diagnostico, LeituraDaIA } from '../components/LaudoModelo';
import CoberturaCampos from '../components/CoberturaCampos';
import PoliticaOperadoras from '../components/PoliticaOperadoras';
import { useAuth } from '../contexts/AuthContext';
import { brl, formatDate, numero } from '../utils/formato';
import { STATUS_MODELO } from '../utils/opcoes';
import { useOrdenacao } from '../hooks/useOrdenacao';

/* ── Marca de verificação, que acompanha todo dado do modelo ───────────── */
function MarcaVerificacao({ modelo }) {
  if (modelo.verificado_aritmeticamente) {
    return (
      <span style={{ fontSize: 13, color: '#1b7a51' }}>
        <i className="bi bi-shield-check me-1" aria-hidden="true" />
        Verificado
      </span>
    );
  }
  return (
    <span
      style={{ fontSize: 13, color: '#9a5b06' }}
      title="Todo dado extraído por este modelo carrega esta marca, para que a conciliação saiba que a confiança é menor."
    >
      <i className="bi bi-shield-exclamation me-1" aria-hidden="true" />
      Não verificado
    </span>
  );
}

function OrigemBadge({ origem }) {
  return origem === 'ia' ? (
    <span style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
      <i className="bi bi-stars me-1" aria-hidden="true" />
      Mapeado por IA
    </span>
  ) : (
    <span style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
      <i className="bi bi-person-gear me-1" aria-hidden="true" />
      Manual
    </span>
  );
}

/* ── O que cada operadora aceita hoje ───────────────────────────────────── */
function ResumoPorOperadora({ modelos }) {
  // Uma operadora pode aceitar MAIS DE UM formato — a GEAP manda XML e XLSX.
  // Quem vai lançar precisa saber disso antes de tentar o upload, e quem
  // administra precisa ver o que ainda falta mapear.
  const porOperadora = modelos.reduce((acc, m) => {
    const grupo = (acc[m.operadora] = acc[m.operadora] || {
      aprovados: [],
      pendentes: 0,
    });
    if (m.status === 'aprovado') grupo.aprovados.push(m);
    else if (m.status === 'aguardando_admin') grupo.pendentes += 1;
    return acc;
  }, {});

  const nomes = Object.keys(porOperadora).sort();
  if (nomes.length === 0) return null;

  return (
    <Painel
      titulo="Formatos aceitos por operadora"
      subtitulo="Uma operadora pode enviar mais de um tipo de arquivo. O sistema reconhece qual é qual no momento do upload."
    >
      <div className="p-4 d-flex flex-column gap-3">
        {nomes.map((nome) => {
          const grupo = porOperadora[nome];
          return (
            <div
              key={nome}
              className="d-flex flex-wrap gap-2 align-items-center justify-content-between"
              style={{ borderBottom: '1px solid var(--ff-border-soft)', paddingBottom: 12 }}
            >
              <div>
                <strong>{nome}</strong>
                {grupo.pendentes > 0 && (
                  <span className="ms-2">
                    <Badge tom="pendente">{grupo.pendentes} aguardando aprovação</Badge>
                  </span>
                )}
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {grupo.aprovados.length === 0 ? (
                  <span style={{ fontSize: 13, color: '#9a5b06' }}>
                    Nenhum formato liberado — a operadora ainda não aceita lançamento
                  </span>
                ) : (
                  grupo.aprovados.map((m) => (
                    <span
                      key={m.id_modelo_arquivo}
                      className="ff-badge ff-badge--confirmado"
                      title={`${m.nome} · versão ${m.versao}`}
                    >
                      {rotuloFormato(m.formato)}
                    </span>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Painel>
  );
}

/** `pdf_posicional` é nome interno; na tela vale o que a pessoa reconhece. */
function rotuloFormato(formato) {
  return (
    {
      pdf_posicional: 'PDF',
      xlsx: 'Planilha (XLSX)',
      xls: 'Planilha (XLS)',
      planilha: 'Planilha',
      xml: 'XML',
    }[formato] || formato
  );
}

/* ── Painel de decisão do admin ────────────────────────────────────────── */
function Aprovacao({ modelo, irmaosPendentes = [], aoDecidir, aoFechar }) {
  const [detalhe, setDetalhe] = useState(null);
  const [erro, setErro] = useState('');
  const [motivo, setMotivo] = useState('');
  const [semValidacao, setSemValidacao] = useState(false);
  const [agindo, setAgindo] = useState(false);

  useEffect(() => {
    detalharModelo(modelo.id_modelo_arquivo)
      .then((r) => setDetalhe(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível abrir o modelo.')));
  }, [modelo.id_modelo_arquivo]);

  async function aprovar() {
    setErro('');
    setAgindo(true);
    try {
      await aprovarModelo(modelo.id_modelo_arquivo, { aprovarSemValidacao: semValidacao });
      aoDecidir();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível aprovar.'));
      setAgindo(false);
    }
  }

  async function rejeitar() {
    if (!motivo.trim()) {
      setErro('Informe o motivo da rejeição — quem cadastrou precisa saber o porquê.');
      return;
    }
    setErro('');
    setAgindo(true);
    try {
      await rejeitarModelo(modelo.id_modelo_arquivo, motivo.trim());
      aoDecidir();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível rejeitar.'));
      setAgindo(false);
    }
  }

  if (!detalhe) {
    return (
      <Painel titulo={`${modelo.operadora} · ${modelo.nome}`}>
        <LoadingSpinner />
      </Painel>
    );
  }

  const diag = detalhe.diagnostico || {};
  const fechou = diag.aprovado;
  const resumo = diag.resumo || {};

  return (
    <Painel
      titulo={`${detalhe.operadora} · ${detalhe.nome} v${detalhe.versao}`}
      subtitulo={`Enviado por ${detalhe.criado_por_email || '—'} · exemplo: ${
        detalhe.arquivo_exemplo_nome || '—'
      }`}
      acoes={
        <Botao variante="secundario" pequeno onClick={aoFechar}>
          Fechar
        </Botao>
      }
    >
      <div className="p-4">
        <Alerta tipo="erro" onFechar={() => setErro('')}>
          {erro}
        </Alerta>

        {/* Um par complementar e UMA decisao. Aprovar so um lado nao gera
            erro nenhum: a operadora passa a aceitar lancamento, e o arquivo
            que ficou para tras e recusado la na frente com "nenhum modelo
            reconhece este arquivo" — sem nada ligando a causa ao efeito. */}
        {modelo.id_conjunto && irmaosPendentes.length > 0 && (
          <Alerta tipo="atencao">
            <strong>Este modelo faz parte de um conjunto.</strong> Ele veio junto com{' '}
            {irmaosPendentes.length === 1 ? 'mais 1 arquivo' : `mais ${irmaosPendentes.length} arquivos`}{' '}
            da {modelo.operadora}
            {modelo.relacao === 'complementares'
              ? ', e eles se completam — o pagamento e a glosa, por exemplo.'
              : ', que trazem o mesmo conteúdo em formatos diferentes.'}{' '}
            Ainda aguardam sua decisão:{' '}
            <span className="ff-mono">
              {irmaosPendentes.map((i) => i.arquivo_exemplo_nome || i.nome).join(', ')}
            </span>
            .{' '}
            {modelo.relacao === 'complementares'
              ? 'Aprovar só parte deixa a operadora funcionando pela metade: o arquivo que ficar para trás será recusado no lançamento.'
              : 'Aprovar mais de um é permitido — o sistema aceita o primeiro que chegar em cada competência e recusa o outro, para não dobrar a baixa.'}
          </Alerta>
        )}

        <Alerta tipo={fechou ? 'ok' : 'atencao'}>
          {fechou ? (
            <>
              <strong>As conferências fecharam.</strong> A soma das linhas bate com os totais
              impressos no próprio documento — é isso que sustenta o modelo.
            </>
          ) : (
            <>
              <strong>As conferências não fecharam.</strong> Aprovar assim marca a operadora
              permanentemente como não verificada aritmeticamente, e a marca acompanha todo
              dado extraído por ela.
            </>
          )}
        </Alerta>

        {/* O diagnóstico vem antes da lista de conferências: o admin decide
            sobre o motivo, não sobre o código da regra que falhou. */}
        {(diag.explicacao || diag.diagnostico_ia) && (
          <div className="mt-3">
            <Diagnostico diagnostico={diag} />
          </div>
        )}

        <div className="mt-3">
          <Conferencias diagnostico={diag} comPainel={false} />
        </div>

        <div className="ff-indicadores mt-3">
          <div>
            <div className="ff-indicador__rotulo">Guias lidas</div>
            <div className="ff-indicador__valor">{numero(resumo.guias)}</div>
          </div>
          <div>
            <div className="ff-indicador__rotulo">Linhas</div>
            <div className="ff-indicador__valor">{numero(resumo.linhas)}</div>
          </div>
          <div>
            <div className="ff-indicador__rotulo">Soma extraída</div>
            <div className="ff-indicador__valor">{brl(resumo.total_extraido)}</div>
          </div>
          {Object.entries(diag.totais_documento || {})
            .filter(([chave]) => chave !== 'total_extraido')
            .map(([chave, valor]) => (
              <div key={chave}>
                <div className="ff-indicador__rotulo">Impresso no documento</div>
                <div className="ff-indicador__valor">{brl(valor)}</div>
              </div>
            ))}
        </div>

        <div className="mt-4">
          <LeituraDaIA diagnostico={diag} />
        </div>

        <details className="mt-3">
          <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
            Especificação de leitura (o que o motor vai aplicar)
          </summary>
          <pre className="ff-mono mt-2" style={{ fontSize: 12, maxHeight: 320, overflow: 'auto' }}>
            {JSON.stringify(detalhe.spec, null, 2)}
          </pre>
        </details>

        {detalhe.raiox_enviado && (
          <details className="mt-3">
            <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              O que foi enviado à IA (auditoria de dados pessoais)
            </summary>
            <p className="mt-2" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
              Este é exatamente o conteúdo que saiu do sistema. Nomes, carteirinhas e demais
              identificadores aparecem mascarados: dígitos viram 0 e letras viram X.
            </p>
            <pre className="ff-mono" style={{ fontSize: 12, maxHeight: 320, overflow: 'auto' }}>
              {JSON.stringify(detalhe.raiox_enviado, null, 2)}
            </pre>
          </details>
        )}

        <hr className="my-4" />

        {!fechou && (
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="sem-validacao"
              checked={semValidacao}
              onChange={(e) => setSemValidacao(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="sem-validacao" style={{ fontSize: 14 }}>
              Aprovar mesmo sem validação aritmética, assumindo a marca permanente de
              não verificado.
            </label>
          </div>
        )}

        <div className="ff-campo mb-3">
          <label htmlFor="motivo-rejeicao">Motivo, em caso de rejeição</label>
          <textarea
            id="motivo-rejeicao"
            className="ff-input"
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: o arquivo-exemplo está incompleto; peça um demonstrativo fechado."
          />
        </div>

        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="ff-btn ff-btn--primario"
            onClick={aprovar}
            disabled={agindo || (!fechou && !semValidacao)}
          >
            {agindo ? 'Processando…' : 'Aprovar e liberar para lançamento'}
          </button>
          <button
            type="button"
            className="ff-btn ff-btn--secundario"
            onClick={rejeitar}
            disabled={agindo}
          >
            Rejeitar
          </button>
        </div>

        {!fechou && !semValidacao && (
          <p className="mt-2" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
            A aprovação está bloqueada porque as conferências não fecharam. Marque a caixa
            acima para assumir a exceção conscientemente.
          </p>
        )}
      </div>
    </Painel>
  );
}

/* ── Confirmar exclusão ─────────────────────────────────────────────────
 *
 * Apagar é irreversível e a tela precisa dizer o que exatamente some. O
 * servidor ainda recusa (409) um modelo que já leu arquivo — este diálogo não
 * substitui essa trava, só evita o clique sem querer.
 */
function ConfirmarExclusao({ modelo, aoFechar, aoExcluir }) {
  const [erro, setErro] = useState('');
  const [agindo, setAgindo] = useState(false);

  const confirmar = async () => {
    setErro('');
    setAgindo(true);
    try {
      await excluirModelo(modelo.id_modelo_arquivo);
      aoExcluir(`Modelo "${modelo.nome}" da ${modelo.operadora} foi excluído.`);
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível excluir o modelo.'));
    } finally {
      setAgindo(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ background: 'rgba(16,24,40,.5)' }} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Excluir modelo</h2>
                <p className="ff-painel__sub">
                  {modelo.operadora} · {modelo.nome} v{modelo.versao}
                </p>
              </div>
              <button type="button" className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <Alerta tipo="erro">{erro}</Alerta>
              <div className="ff-aviso ff-aviso--atencao" style={{ margin: 0 }}>
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                <div>
                  Somem a especificação de leitura, o diagnóstico e o{' '}
                  <strong>arquivo-exemplo guardado</strong>. Não há como desfazer.
                  {modelo.status === 'aprovado' && (
                    <>
                      {' '}
                      Para apenas tirar de uso mantendo o histórico, use{' '}
                      <strong>Inativar</strong>.
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" onClick={aoFechar} disabled={agindo}>
                Cancelar
              </Botao>
              <button
                type="button"
                className="ff-btn ff-btn--perigo"
                onClick={confirmar}
                disabled={agindo}
              >
                {agindo ? 'Excluindo…' : 'Excluir definitivamente'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Tabela de modelos ─────────────────────────────────────────────────── */
function TabelaModelos({ modelos, aoAbrir, aoNovaVersao, aoSubmeter, acoesAdmin }) {
  const ord = useOrdenacao(modelos);
  return (
    <Tabela estreita>
      <thead>
        <tr>
          <Th campo="operadora" estado={ord}>Operadora</Th>
          <Th campo="nome" estado={ord}>Modelo</Th>
          <Th campo="versao" estado={ord} alinhar="right">Versão</Th>
          <Th campo="formato" estado={ord}>Formato</Th>
          <Th campo="origem" estado={ord}>Origem</Th>
          <Th campo="status" estado={ord}>Status</Th>
          <Th campo="verificado_aritmeticamente" estado={ord}>Conferência</Th>
          {aoAbrir ? (
            <Th alinhar="right">Ação</Th>
          ) : (
            <Th campo="aprovado_em" estado={ord} alinhar="right">
              Aprovado em
            </Th>
          )}
          {acoesAdmin && <Th alinhar="right">Administração</Th>}
        </tr>
      </thead>
      <tbody>
        {ord.ordenados.map((m) => {
          const cfg = STATUS_MODELO[m.status] || { label: m.status, tom: 'arquivado' };
          return (
            <tr key={m.id_modelo_arquivo}>
              <td className="col-principal">{m.operadora}</td>
              <td>{m.nome}</td>
              <td className="col-qtd">v{m.versao}</td>
              <td className="col-meta">{m.formato}</td>
              <td>
                <OrigemBadge origem={m.origem} />
              </td>
              <td>
                <Badge tom={cfg.tom}>{cfg.label}</Badge>
              </td>
              <td>
                {m.status === 'aprovado' ? (
                  <MarcaVerificacao modelo={m} />
                ) : m.conferencias_fecharam == null ? (
                  <span style={{ fontSize: 13, color: 'var(--ff-muted)' }}>—</span>
                ) : (
                  <Badge tom={m.conferencias_fecharam ? 'confirmado' : 'reprovado'}>
                    {m.conferencias_fecharam ? 'Fechou' : 'Não fechou'}
                  </Badge>
                )}
              </td>
              <td className="col-data" style={{ textAlign: 'right' }}>
                {aoAbrir ? (
                  <Botao variante="secundario" pequeno onClick={() => aoAbrir(m)}>
                    Revisar
                  </Botao>
                ) : (
                  <div className="d-flex gap-2 justify-content-end align-items-center">
                    <span>{formatDate(m.aprovado_em)}</span>
                    {/* O caminho que faltava. Um modelo mapeado e conferido
                        que fica em rascunho sem forma de avancar e um beco
                        sem saida: a tela do cadastro mandava "envie pela tela
                        de Modelos", e aqui nao havia esse botao. */}
                    {aoSubmeter && (m.status === 'rascunho' || m.status === 'rejeitado') && (
                      <Botao
                        variante="primario"
                        pequeno
                        onClick={() => aoSubmeter(m)}
                        title={
                          m.id_conjunto
                            ? 'Envia ao administrador todos os modelos deste conjunto.'
                            : 'Envia ao administrador para liberar a operadora no lançamento.'
                        }
                      >
                        Enviar para aprovação
                      </Botao>
                    )}
                    {aoNovaVersao && (
                      <Botao
                        variante="secundario"
                        pequeno
                        onClick={() => aoNovaVersao(m)}
                        title="A operadora mudou o layout? Cadastre a versão nova; a atual continua valendo até você definir a vigência."
                      >
                        Nova versão
                      </Botao>
                    )}
                  </div>
                )}
              </td>
              {acoesAdmin && (
                <td className="col-data" style={{ textAlign: 'right' }}>
                  <div className="d-flex gap-2 justify-content-end">
                    {/* Inativar só faz sentido para um modelo em uso; um
                        rascunho ou rejeitado se resolve apagando. */}
                    {m.status === 'aprovado' && (
                      <Botao
                        variante="secundario"
                        pequeno
                        onClick={() => acoesAdmin.inativar(m, false)}
                        title="Tira de uso imediatamente, sem apagar. Os arquivos já lidos continuam explicáveis."
                      >
                        Inativar
                      </Botao>
                    )}
                    {m.status === 'inativo' && (
                      <Botao
                        variante="secundario"
                        pequeno
                        onClick={() => acoesAdmin.inativar(m, true)}
                        title="Volta a aceitar lançamento."
                      >
                        Reativar
                      </Botao>
                    )}
                    <Botao
                      variante="secundario"
                      pequeno
                      onClick={() => acoesAdmin.excluir(m)}
                      title="Apaga o modelo e o arquivo-exemplo. Recusado se o modelo já leu algum arquivo."
                    >
                      Excluir
                    </Botao>
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </Tabela>
  );
}

export default function Modelos() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [modelos, setModelos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState(
    location.state?.submetido ? 'Modelo enviado para aprovação do administrador.' : ''
  );
  const [aba, setAba] = useState('cadastrados');
  const [emRevisao, setEmRevisao] = useState(null);
  const [ia, setIa] = useState(null);
  const [aExcluir, setAExcluir] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    listarTodosModelos()
      .then((r) => setModelos(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar os modelos.')))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    carregar();
    iaDisponivel()
      .then((r) => setIa(r.data))
      .catch(() => setIa({ disponivel: false }));
  }, [carregar]);

  const trocarAtividade = async (m, reativar) => {
    setErro('');
    try {
      await inativarModelo(m.id_modelo_arquivo, reativar);
      setAviso(
        reativar
          ? `"${m.nome}" da ${m.operadora} voltou a aceitar lançamento.`
          : `"${m.nome}" da ${m.operadora} foi inativado e não aceita mais lançamento.`
      );
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível alterar o modelo.'));
    }
  };

  const acoesAdmin = isAdmin
    ? { inativar: trocarAtividade, excluir: setAExcluir }
    : undefined;

  /* Enviar ao admin. Quando o modelo pertence a um conjunto, vai o conjunto
     INTEIRO — aprovar so parte de um par complementar deixa a operadora
     funcionando pela metade, recusando no lancamento o arquivo que ficou
     para tras, sem nada na tela explicando por que. */
  const submeter = async (m) => {
    setErro('');
    try {
      if (m.id_conjunto) {
        const { data } = await submeterConjunto(m.id_conjunto);
        const trocados = data.substituidos?.length || 0;
        setAviso(
          `${data.enviados.length} modelo(s) do conjunto "${m.conjunto}" ` +
            'enviados para aprovação.' +
            // Versões anteriores do mesmo modelo ficam para trás de
            // propósito: mandar v1 e v2 ao admin seria pedir uma decisão
            // que não existe, e aprovar as duas deixaria dois modelos
            // vigentes para o mesmo arquivo.
            (trocados
              ? ` ${trocados} versão(ões) anterior(es) ficaram de fora — já foram substituídas.`
              : '')
        );
      } else {
        await submeterModelo(m.id_modelo_arquivo);
        setAviso(`Modelo "${m.nome}" enviado para aprovação.`);
      }
      await carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível enviar para aprovação.'));
    }
  };

  const pendentes = modelos.filter((m) => m.status === 'aguardando_admin');
  const listaAba = aba === 'pendentes' ? pendentes : modelos;

  if (carregando) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        titulo="Modelos de arquivo"
        subtitulo="Cada operadora manda um formato próprio. O modelo descreve como ler — e é cadastro, não código."
        acoes={
          ia?.disponivel ? (
            <Botao icone="bi-plus-lg" onClick={() => navigate('/modelos/novo')}>
              Cadastrar operadora
            </Botao>
          ) : null
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      {isAdmin && (
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${aba === 'cadastrados' ? 'active' : ''}`}
              onClick={() => {
                setAba('cadastrados');
                setEmRevisao(null);
              }}
            >
              Cadastrados
              <span className="ms-2 ff-mono" style={{ fontSize: 12 }}>
                {modelos.length}
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${aba === 'pendentes' ? 'active' : ''}`}
              onClick={() => setAba('pendentes')}
            >
              Aguardando aprovação
              {pendentes.length > 0 && (
                <span className="ms-2">
                  <Badge tom="pendente">{pendentes.length}</Badge>
                </span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${aba === 'cobertura' ? 'active' : ''}`}
              onClick={() => {
                setAba('cobertura');
                setEmRevisao(null);
              }}
            >
              Cobertura de campos
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${aba === 'politica' ? 'active' : ''}`}
              onClick={() => {
                setAba('politica');
                setEmRevisao(null);
              }}
            >
              Política por operadora
            </button>
          </li>
        </ul>
      )}

      {aba === 'politica' ? (
        <PoliticaOperadoras />
      ) : aba === 'cobertura' ? (
        <CoberturaCampos />
      ) : emRevisao ? (
        <Aprovacao
          modelo={emRevisao}
          irmaosPendentes={pendentes.filter(
            (m) =>
              m.id_conjunto &&
              m.id_conjunto === emRevisao.id_conjunto &&
              m.id_modelo_arquivo !== emRevisao.id_modelo_arquivo
          )}
          aoFechar={() => setEmRevisao(null)}
          aoDecidir={() => {
            setEmRevisao(null);
            setAviso('Decisão registrada.');
            carregar();
          }}
        />
      ) : listaAba.length === 0 ? (
        <Painel>
          <Vazio
            icone="bi-diagram-3"
            titulo={
              aba === 'pendentes'
                ? 'Nenhum modelo aguardando aprovação'
                : 'Nenhum modelo cadastrado'
            }
            descricao={
              aba === 'pendentes'
                ? 'Quando alguém cadastrar uma operadora, ela aparece aqui para sua decisão.'
                : 'Cadastre uma operadora subindo um demonstrativo de exemplo.'
            }
          />
        </Painel>
      ) : aba === 'cadastrados' ? (
        <>
          <ResumoPorOperadora modelos={modelos} />
          <Painel titulo="Modelos cadastrados">
            <TabelaModelos
              modelos={listaAba}
              acoesAdmin={acoesAdmin}
              aoSubmeter={submeter}
              aoNovaVersao={(m) =>
                navigate('/modelos/novo', {
                  state: { operadora: m.operadora, novaVersaoDe: m.nome },
                })
              }
            />
          </Painel>
        </>
      ) : (
        <Painel
          titulo={aba === 'pendentes' ? 'Aguardando sua aprovação' : 'Modelos cadastrados'}
          subtitulo={
            aba === 'pendentes'
              ? 'Cada modelo traz o resultado da conferência contra o arquivo-exemplo. É isso que decide, não a aparência da tabulação.'
              : undefined
          }
        >
          <TabelaModelos
            modelos={listaAba}
            aoAbrir={aba === 'pendentes' ? setEmRevisao : undefined}
          />
        </Painel>
      )}

      {aExcluir && (
        <ConfirmarExclusao
          modelo={aExcluir}
          aoFechar={() => setAExcluir(null)}
          aoExcluir={(msg) => {
            setAExcluir(null);
            setAviso(msg);
            carregar();
          }}
        />
      )}
    </>
  );
}
